import { drawCanvasIcon, drawFilterLinesMark } from "./canvas-icons";
import { pickContrastingColor } from "./color-contrast";
import { volcaColor, volcaColorAlpha } from "./spreadsheet-theme";

// Flags a cell that carries a persistent manual edit with a bold right-triangle
// in its top-right corner — a punchier take on the admin grid's edited-cell
// corner marker. Painted on the grid canvas via the viewer's drawCell, on top
// of the already-rendered cell content, so it never tints the cell background
// or clips its text. A soft drop shadow lifts it off the cell so it draws the
// eye; a thin light stroke on the exposed diagonal keeps the edge crisp.
const MARKER_SIZE_PX = 13;
const copiedRangeBorderColor = () => volcaColor("--green-60");
const labelWidthCache = new Map<string, number>();

const measureLabelWidth = (
  ctx: CanvasRenderingContext2D,
  label: string,
  font: string,
) => {
  const key = `${font}\u0000${label}`;
  const cached = labelWidthCache.get(key);
  if (cached !== undefined) return cached;

  ctx.font = font;
  const width = ctx.measureText(label).width;
  labelWidthCache.set(key, width);
  return width;
};

export const drawCopiedRangeBorder = (
  ctx: CanvasRenderingContext2D,
  rect: { x: number; y: number; width: number; height: number },
  cell: { col: number; row: number },
  range: { x: number; y: number; width: number; height: number },
): void => {
  const isInsideRange =
    cell.col >= range.x &&
    cell.col < range.x + range.width &&
    cell.row >= range.y &&
    cell.row < range.y + range.height;
  if (!isInsideRange) return;

  const isTop = cell.row === range.y;
  const isBottom = cell.row === range.y + range.height - 1;
  const isLeft = cell.col === range.x;
  const isRight = cell.col === range.x + range.width - 1;
  if (!isTop && !isBottom && !isLeft && !isRight) return;

  const inset = 1;
  const left = rect.x + inset;
  const right = rect.x + rect.width - inset;
  const top = rect.y + inset;
  const bottom = rect.y + rect.height - inset;

  ctx.save();
  ctx.beginPath();
  if (isTop) {
    ctx.moveTo(left, top);
    ctx.lineTo(right, top);
  }
  if (isRight) {
    ctx.moveTo(right, top);
    ctx.lineTo(right, bottom);
  }
  if (isBottom) {
    ctx.moveTo(right, bottom);
    ctx.lineTo(left, bottom);
  }
  if (isLeft) {
    ctx.moveTo(left, bottom);
    ctx.lineTo(left, top);
  }
  ctx.setLineDash([5, 3]);
  ctx.lineWidth = 2;
  ctx.strokeStyle = copiedRangeBorderColor();
  ctx.stroke();
  ctx.restore();
};

export const drawOverrideCornerMarker = (
  ctx: CanvasRenderingContext2D,
  rect: { x: number; y: number; width: number; height: number },
  color: string,
): void => {
  const right = rect.x + rect.width;
  const topRight = { x: right, y: rect.y };
  const left = { x: right - MARKER_SIZE_PX, y: rect.y };
  const bottom = { x: right, y: rect.y + MARKER_SIZE_PX };

  ctx.save();
  ctx.beginPath();
  ctx.moveTo(left.x, left.y);
  ctx.lineTo(topRight.x, topRight.y);
  ctx.lineTo(bottom.x, bottom.y);
  ctx.closePath();

  // Soft lift so the marker pops off the cell. The tint is the darkest neutral
  // rather than a blue-black, so the shadow stays hueless over the coloured
  // fills a generated payroll sheet paints.
  ctx.shadowColor = volcaColorAlpha("--gray-100", 0.35);
  ctx.shadowBlur = 4;
  ctx.shadowOffsetX = -1;
  ctx.shadowOffsetY = 1;
  ctx.fillStyle = color;
  ctx.fill();

  // Crisp light edge on the exposed diagonal (no shadow on the stroke).
  ctx.shadowColor = "transparent";
  ctx.beginPath();
  ctx.moveTo(left.x, left.y);
  ctx.lineTo(bottom.x, bottom.y);
  ctx.lineWidth = 1.5;
  ctx.strokeStyle = "rgba(255, 255, 255, 0.9)";
  ctx.stroke();
  ctx.restore();
};

// Flags a cell with an open comment thread: the same triangle as the edit
// marker, but in the top-LEFT corner so a commented edit shows both.
const commentMarkerColor = () => volcaColor("--orange-50");

export const drawCommentCornerMarker = (
  ctx: CanvasRenderingContext2D,
  rect: { x: number; y: number; width: number; height: number },
): void => {
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(rect.x, rect.y);
  ctx.lineTo(rect.x + MARKER_SIZE_PX, rect.y);
  ctx.lineTo(rect.x, rect.y + MARKER_SIZE_PX);
  ctx.closePath();
  ctx.shadowColor = volcaColorAlpha("--gray-100", 0.35);
  ctx.shadowBlur = 4;
  ctx.shadowOffsetX = 1;
  ctx.shadowOffsetY = 1;
  ctx.fillStyle = commentMarkerColor();
  ctx.fill();

  ctx.shadowColor = "transparent";
  ctx.beginPath();
  ctx.moveTo(rect.x + MARKER_SIZE_PX, rect.y);
  ctx.lineTo(rect.x, rect.y + MARKER_SIZE_PX);
  ctx.lineWidth = 1.5;
  ctx.strokeStyle = "rgba(255, 255, 255, 0.9)";
  ctx.stroke();
  ctx.restore();
};

/**
 * Repaints a selected column's header grey.
 *
 * Glide fills a selected header with `accentColor` at full size, not as a ring
 * — so selecting a column put a solid blue block above the sheet, louder than
 * anything else on screen and louder than the selection it was describing. Grey
 * says the same thing quietly, and continues the ramp the other header states
 * already climb: at rest `--gray-5`, hovered `--gray-10`, holding the selected
 * cell `--gray-15`, wholly selected `--gray-20`.
 *
 * Done here rather than in the theme because `accentColor` is shared with the
 * cell selection ring, which stays blue. Glide fills the header before handing
 * it to this callback, so this paints over that fill; the title is then drawn by
 * Glide in `textHeaderSelected`, which the theme sets to normal ink to match.
 */
export const drawSelectedHeaderBackground = (
  ctx: CanvasRenderingContext2D,
  rect: { x: number; y: number; width: number; height: number },
  /**
   * `--gray-20` for a wholly selected column, `--gray-15` for one a selected
   * RANGE merely passes through. Glide marks only the anchor cell's column, so
   * dragging a block across five columns lit one header and left the other four
   * at rest — the axis stopped describing the selection as soon as it grew past
   * a single cell.
   */
  isWholeColumnSelected = true,
): void =>
  fillCellBackground(
    ctx,
    rect,
    volcaColor(isWholeColumnSelected ? "--gray-20" : "--gray-15"),
  );

/**
 * Tints the row-number gutter cell of the row holding the selected cell.
 *
 * Glide marks only one axis: selecting a cell fills its column header with
 * `bgHeaderHasFocus`, while the gutter draws its number flat, in `textLight`,
 * whatever is selected. So the grid told you which column you were in and never
 * which row. Every other spreadsheet marks both, and on a sheet this wide the
 * row is the harder one to keep track of.
 *
 * Deliberately the SAME `--gray-15` the column header takes, so the two axes
 * read as one crosshair rather than two unrelated highlights.
 *
 * Painted inside the row-marker renderer because the viewer's `drawCell` cannot
 * reach here: marker cells are "inner only", and Glide skips the draw callback
 * for them entirely.
 */
export const drawSelectedRowMarkerBackground = (
  ctx: CanvasRenderingContext2D,
  rect: { x: number; y: number; width: number; height: number },
  /**
   * The row axis climbs the same two steps the column header does: `--gray-15`
   * when the row merely holds the selected cell, `--gray-20` when the whole row
   * is selected. Without the second step a fully selected row kept the rest
   * fill and took Glide's blue row wash over it, so the two axes disagreed —
   * selecting a column greyed its header, selecting a row tinted its gutter blue.
   */
  isWholeRowSelected = false,
): void =>
  fillCellBackground(
    ctx,
    rect,
    volcaColor(isWholeRowSelected ? "--gray-20" : "--gray-15"),
  );

const fillCellBackground = (
  ctx: CanvasRenderingContext2D,
  rect: { x: number; y: number; width: number; height: number },
  color: string,
): void => {
  ctx.save();
  ctx.fillStyle = color;
  ctx.fillRect(rect.x, rect.y, rect.width, rect.height);
  ctx.restore();
};

/* ---------------------------------------------------------------------------
 * Table-region column controls
 *
 * Drawn on the header cell of a saved table region to show that the column can
 * be filtered or sorted, and what is currently applied to it. Everything here is
 * a vector path rather than a text glyph: a glyph at this size renders at the
 * mercy of the platform font and collapses into an indistinct blob.
 *
 * Colors come from the cell's OWN resolved theme, because a generated payroll
 * header can be any fill the script chose — Dean's is near-black navy, another
 * business may be pale grey. The idle mark derives from `textColor` and the
 * active chip inverts out of the fill, so both stay legible on either without
 * picking a colour that only works on one.
 * ------------------------------------------------------------------------- */

const CHIP_HEIGHT_PX = 21;
const CHIP_RADIUS_PX = 5;
const CHIP_ICON_PX = 15;
const IDLE_MARK_PX = 13;
// Glide's own horizontal cell padding, plus the breathing room an idle mark needs
// before it starts crowding a centred heading.
const HEADER_TEXT_PADDING_PX = 8;
const IDLE_MARK_GAP_PX = 10;
const EDGE_INSET_PX = 5;
// Below this the cell is too narrow to carry both icons plus its own label, so
// only the primary one is drawn.
const TWO_ICON_MIN_WIDTH_PX = 128;

export interface ColumnControlState {
  filtered: boolean;
  sortDirection: "asc" | "desc" | null;
  /** The header cell's resolved text color, used for the idle mark. */
  textColor: string;
  /** The header cell's resolved fill, used to mask any label running underneath. */
  backgroundColor: string;
  /** The header's own text, measured to decide whether an idle mark fits beside it. */
  label: string;
  /** The canvas font Glide drew that text with, so the measurement matches. */
  font: string;
}

/**
 * A fully-transparent version of the same color, for the far end of a fade.
 * Interpolating towards a bare `transparent` keyword instead would ramp through
 * transparent BLACK, greying the fade on a light header.
 */
export const transparentVersionOf = (color: string): string => {
  const hex = color.trim();
  if (/^#[0-9a-f]{6}$/i.test(hex)) return `${hex}00`;
  if (/^#[0-9a-f]{3,4}$/i.test(hex)) {
    const r = hex.charAt(1);
    const g = hex.charAt(2);
    const b = hex.charAt(3);
    return `#${r}${r}${g}${g}${b}${b}00`;
  }
  if (/^#[0-9a-f]{8}$/i.test(hex)) return `${hex.slice(0, 7)}00`;

  const channels = hex.match(/^rgba?\(([^)]+)\)$/i)?.[1];
  if (channels) {
    const [r, g, b] = channels.split(",").map((part) => part.trim());
    return `rgba(${r}, ${g}, ${b}, 0)`;
  }

  return "rgba(255, 255, 255, 0)";
};

/**
 * Clears room for the marker by fading the header label out into the cell fill
 * rather than butting up against it. A hard mask reads as a truncation bug —
 * "Department" chopped to "Departmen" — where a short gradient reads as the
 * label simply running out of room.
 */
const fadeOutLabelBehind = (
  ctx: CanvasRenderingContext2D,
  rect: { x: number; y: number; width: number; height: number },
  markerLeft: number,
  backgroundColor: string,
): void => {
  const fadeWidth = 12;
  const fadeStart = Math.max(rect.x, markerLeft - fadeWidth);
  const gradient = ctx.createLinearGradient(fadeStart, 0, markerLeft, 0);
  gradient.addColorStop(0, transparentVersionOf(backgroundColor));
  gradient.addColorStop(1, backgroundColor);

  ctx.fillStyle = gradient;
  ctx.fillRect(fadeStart, rect.y + 1, markerLeft - fadeStart, rect.height - 2);

  ctx.fillStyle = backgroundColor;
  ctx.fillRect(
    markerLeft,
    rect.y + 1,
    rect.x + rect.width - markerLeft,
    rect.height - 2,
  );
};

const roundedRectPath = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
): void => {
  const r = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + width, y, x + width, y + height, r);
  ctx.arcTo(x + width, y + height, x, y + height, r);
  ctx.arcTo(x, y + height, x, y, r);
  ctx.arcTo(x, y, x + width, y, r);
  ctx.closePath();
};

/**
 * Paints the filter/sort affordance on a region header cell.
 *
 * Idle columns get a quiet three-line mark; a column with something applied gets
 * a solid chip carrying the matching Heroicon — a funnel for a filter, bars-and-
 * arrow for a sort, both where the column is wide enough. The chip states WHICH
 * control is on, where the mark it replaced only said "something".
 *
 * Every colour is derived from the cell's own fill, so the mark holds up on a
 * near-black header, a pale grey one, or a saturated brand colour that happens to
 * sit near the accent.
 */
export const drawColumnControlMarker = (
  ctx: CanvasRenderingContext2D,
  rect: { x: number; y: number; width: number; height: number },
  {
    filtered,
    sortDirection,
    textColor,
    backgroundColor,
    label,
    font,
  }: ColumnControlState,
): void => {
  const active = filtered || sortDirection !== null;
  const centerY = Math.round(rect.y + rect.height / 2);
  const right = rect.x + rect.width - EDGE_INSET_PX;

  ctx.save();
  // Never let the mark bleed into the neighbouring column on a narrow header.
  ctx.beginPath();
  ctx.rect(rect.x, rect.y, rect.width, rect.height);
  ctx.clip();

  if (!active) {
    // An idle mark is an invitation, not information, so it is never worth
    // clipping the heading for. Generated payroll columns are auto-sized to their
    // own titles: "Total Hours" fills its column exactly, and a mark there would
    // dim it to "Tota Hour" purely to advertise a control the user finds by
    // clicking. Measure first and only offer the mark where it genuinely fits.
    const labelWidth = measureLabelWidth(ctx, label, font);
    const freeSpace =
      rect.width - labelWidth - HEADER_TEXT_PADDING_PX * 2 - IDLE_MARK_PX;
    if (freeSpace < IDLE_MARK_GAP_PX) {
      ctx.restore();
      return;
    }

    drawFilterLinesMark(ctx, {
      centerX: right - IDLE_MARK_PX / 2,
      centerY,
      width: IDLE_MARK_PX,
      // The header's own text colour is guaranteed to read against its own fill.
      color: textColor,
      alpha: 0.6,
    });
    ctx.restore();
    return;
  }

  const showBoth =
    filtered && sortDirection !== null && rect.width >= TWO_ICON_MIN_WIDTH_PX;
  const chipHeight = Math.min(CHIP_HEIGHT_PX, rect.height - 4);
  const chipWidth = showBoth ? 38 : 24;
  const chipX = right - chipWidth;
  const chipY = Math.round(centerY - chipHeight / 2);

  // The chip inverts out of the header rather than tinting it. A generated
  // payroll header can be any fill the script chose, and a coloured chip has to
  // hold against all of them — on Dean's near-black navy an accent-filled pill
  // put a third hue in one header cell. White is the one fill that reads as
  // "control" without claiming a colour role, and where the header is itself
  // pale the pair flips to near-black so the chip never dissolves into it.
  const chipFill = pickContrastingColor(backgroundColor, [
    volcaColor("--gray-0"),
    volcaColor("--gray-100"),
  ]);
  // Ink takes the header's OWN fill wherever that carries on the chip, so the
  // mark belongs to the sheet it sits in rather than to us, and drops to plain
  // ink where the two are too close. 3:1 is the WCAG floor for a UI graphic.
  const chipInk = pickContrastingColor(chipFill, [backgroundColor], 3);

  fadeOutLabelBehind(ctx, rect, chipX - 3, backgroundColor);

  ctx.fillStyle = chipFill;
  roundedRectPath(ctx, chipX, chipY, chipWidth, chipHeight, CHIP_RADIUS_PX);
  ctx.fill();

  // Hairline ring: cheap insurance that the chip keeps a visible edge even when
  // its fill and the header fill land at a similar lightness.
  ctx.globalAlpha = 0.25;
  ctx.strokeStyle = chipInk;
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.globalAlpha = 1;

  const sortIcon = sortDirection === "asc" ? "barsArrowUp" : "barsArrowDown";

  if (showBoth) {
    drawCanvasIcon(ctx, "funnel", {
      centerX: chipX + chipWidth * 0.31,
      centerY,
      size: CHIP_ICON_PX - 1,
      color: chipInk,
    });
    drawCanvasIcon(ctx, sortIcon, {
      centerX: chipX + chipWidth * 0.69,
      centerY,
      size: CHIP_ICON_PX - 1,
      color: chipInk,
    });
  } else if (filtered) {
    // When the column is too narrow for both, the funnel wins. A sort announces
    // itself in the row order, but a filter is invisible — nothing on screen
    // tells you rows are missing — so it is the one that must always show.
    drawCanvasIcon(ctx, "funnel", {
      centerX: chipX + chipWidth / 2,
      centerY,
      size: CHIP_ICON_PX,
      color: chipInk,
    });
  } else {
    drawCanvasIcon(ctx, sortIcon, {
      centerX: chipX + chipWidth / 2,
      centerY,
      size: CHIP_ICON_PX,
      color: chipInk,
    });
  }

  ctx.restore();
};
